import { useEffect, useState } from "react";

/**
 * ProcessingLoader — Agenzee's shared "AI is working" state.
 * Matches the visual style of the Onboarding "Reading your description…" card.
 * - Accessible: aria-live="polite", aria-busy="true", role="status".
 * - Honours prefers-reduced-motion: with RM, keeps state visible without pulsing/animated bars.
 * - No emoji, no decorative em-dashes.
 */
export const ProcessingLoader = ({
  title,
  description,
  eyebrow,
  testId = "processing-loader",
  className = "",
}) => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const dotClass = reduced ? "" : "animate-pulse";
  const barClass = reduced ? "" : "animate-pulse";

  return (
    <div
      className={`card-soft mt-6 p-8 sm:p-12 ${className}`}
      role="status"
      aria-live="polite"
      aria-busy="true"
      data-testid={testId}
    >
      <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.08em] text-[#9A9591] uppercase">
        <span className={`w-1.5 h-1.5 rounded-full bg-[#96691E] inline-block ${dotClass}`} />
        {eyebrow || "Agenzee is working"}
      </div>
      <p className="mt-5 text-lg font-bold text-[#181621]" data-testid={`${testId}-title`}>{title}</p>
      {description ? (
        <p className="text-[14px] text-[#54505B] mt-1" data-testid={`${testId}-desc`}>{description}</p>
      ) : null}
      <div className="mt-8 space-y-3" aria-hidden="true">
        {[100, 85, 70, 55].map((w, i) => (
          <div
            key={i}
            className={`h-9 rounded-lg bg-[#F3EFE7] ${barClass}`}
            style={{ width: `${w}%`, animationDelay: reduced ? undefined : `${i * 0.15}s` }}
          />
        ))}
      </div>
    </div>
  );
};

export default ProcessingLoader;
